/* Ritual Walkthrough — custom section behavior.
   One step open at a time; the desktop media frame crossfades in sync.
   The Mux engine loads only when the section approaches the viewport;
   the active step's video starts as soon as the visitor reaches the
   section, pauses when they leave, and only the copy that is actually
   rendered (inline on mobile, frame on desktop) ever streams. */

if (!window.customElements.get('ritual-walkthrough')) {
  class RitualWalkthrough extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-rw-step]'));
      this.frames = Array.from(this.querySelectorAll('[data-rw-frame]'));
      this.index = 0;
      this.inView = false;
      this.advanceView = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.steps.forEach((step, i) => {
        const header = step.querySelector('.ritual-walkthrough__step-header');
        header.addEventListener('click', () => {
          this.stopAutoAdvance();
          this.select(i);
        });
      });

      if (this.querySelector('mux-video')) {
        this.loadObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadObserver.disconnect();
            this.loadObserver = null;
            this.loadMuxEngine();
          }
        }, { rootMargin: '600px 0px' });
        this.loadObserver.observe(this);
      }

      this.viewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          this.advanceView = entry.intersectionRatio >= 0.4;
        });
        this.syncMedia();
      }, { threshold: [0, 0.4] });
      this.viewObserver.observe(this);

      this.setupAutoAdvance();
    }

    disconnectedCallback() {
      this.stopAutoAdvance();
      this.removeGestureRecovery();
      if (this.loadObserver) this.loadObserver.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
    }

    select(index) {
      if (index === this.index) {
        return;
      }

      this.index = index;

      this.steps.forEach((step, i) => {
        const open = i === index;
        step.classList.toggle('is-open', open);
        const header = step.querySelector('.ritual-walkthrough__step-header');
        header.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      this.frames.forEach((frame, i) => {
        frame.classList.toggle('is-active', i === index);
      });

      this.syncMedia();
    }

    /* Play the active step's rendered video, pause everything else,
       and pre-buffer the next step so toggles feel instant. */
    syncMedia() {
      const mediaOf = (root) => Array.from(root.querySelectorAll('video, mux-video'));

      this.steps.forEach((step, i) => {
        mediaOf(step).forEach((video) => this.setPlayback(video, i === this.index));
      });
      this.frames.forEach((frame, i) => {
        mediaOf(frame).forEach((video) => this.setPlayback(video, i === this.index));
      });

      if (this.steps.length > 1) {
        const next = (this.index + 1) % this.steps.length;
        mediaOf(this.steps[next]).forEach((video) => this.warm(video));
        if (this.frames[next]) mediaOf(this.frames[next]).forEach((video) => this.warm(video));
      }
    }

    setPlayback(video, active) {
      if (typeof video.play !== 'function') return;

      const rendered = video.checkVisibility
        ? video.checkVisibility()
        : video.offsetParent !== null;

      if (active && rendered && !this.reducedMotion) {
        this.revealOnFirstFrame(video);
        if (this.inView) {
          /* play() implies a full load — never touch preload here: the
             load a preload change triggers aborts this very play() call. */
          this.tryPlay(video);
        } else {
          /* Buffer ahead: the engine arrives ~600px before the section, so
             the active video is ready the moment the visitor reaches it. */
          if (video.preload === 'none') video.preload = 'auto';
          if (!video.paused) video.pause();
        }
      } else if (!video.paused) {
        video.pause();
      }
    }

    tryPlay(video) {
      const p = video.play();
      if (!p) return;
      p.then(() => {
        this.classList.remove('rw-needs-tap');
        this.removeGestureRecovery();
      }).catch((err) => {
        if (err && err.name === 'NotAllowedError') {
          /* Autoplay refused by policy (iOS Low Power Mode, data saver).
             Show a quiet play chip and start playback inside the
             visitor's next tap, where play() is always permitted. */
          this.classList.add('rw-needs-tap');
          this.armGestureRecovery();
          return;
        }

        /* Typically AbortError: play() landed while the source was still
           attaching. Re-sync once the media can play (with a timed
           fallback) so the active step recovers on its own. */
        if (video.rwRetryArmed) return;
        video.rwRetries = (video.rwRetries || 0) + 1;
        if (video.rwRetries > 3) return;
        video.rwRetryArmed = true;

        let timer;
        const replay = () => {
          video.removeEventListener('canplay', replay);
          clearTimeout(timer);
          video.rwRetryArmed = false;
          this.syncMedia();
        };
        video.addEventListener('canplay', replay, { once: true });
        timer = setTimeout(replay, 1200);
      });
    }

    armGestureRecovery() {
      if (this.onGestureRecovery) return;
      this.onGestureRecovery = () => {
        this.removeGestureRecovery();
        this.syncMedia();
      };
      document.addEventListener('touchend', this.onGestureRecovery, true);
      document.addEventListener('click', this.onGestureRecovery, true);
    }

    removeGestureRecovery() {
      if (!this.onGestureRecovery) return;
      document.removeEventListener('touchend', this.onGestureRecovery, true);
      document.removeEventListener('click', this.onGestureRecovery, true);
      this.onGestureRecovery = null;
    }

    /* Manifest + first segment only — cheap insurance for the next tap. */
    warm(video) {
      if (typeof video.play !== 'function') return;
      if (video.preload === 'none') video.preload = 'metadata';
    }

    /* The video layer stays transparent over the poster until it is
       actually rendering frames, so the hand-off is imperceptible.
       'timeupdate' backs up 'playing' in case playback was already
       running before the listeners attached. */
    revealOnFirstFrame(video) {
      if (video.dataset.rwReveal) return;
      video.dataset.rwReveal = '1';
      const reveal = () => video.classList.add('is-live');
      if (!video.paused && video.currentTime > 0) reveal();
      video.addEventListener('playing', reveal, { once: true });
      video.addEventListener('timeupdate', reveal, { once: true });
    }

    setupAutoAdvance() {
      if (!this.hasAttribute('data-auto-advance') || this.reducedMotion || this.steps.length < 2) return;

      this.timer = setInterval(() => {
        if (!this.advanceView || document.hidden) return;
        this.select((this.index + 1) % this.steps.length);
      }, 5000);
    }

    stopAutoAdvance() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    loadMuxEngine() {
      if (!window.customElements.get('mux-video') && !document.querySelector('script[data-mux-video-lib]')) {
        const script = document.createElement('script');
        script.src = this.dataset.muxLib;
        script.defer = true;
        script.setAttribute('data-mux-video-lib', '');
        document.head.appendChild(script);
      }

      /* Once the elements upgrade, re-run playback sync so the active
         step autoplays even though it wasn't playable at connect time. */
      window.customElements.whenDefined('mux-video').then(() => this.syncMedia());
    }
  }

  window.customElements.define('ritual-walkthrough', RitualWalkthrough);
}
